import { useQuery } from '@tanstack/react-query';
import { Form, SelectProps } from 'antd';
import { FormProps } from 'antd/lib';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCountryContext } from '@/contexts/CountryContext';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useFormInitialSet from '@/hooks/useFormInitialSet';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface UseShipperLocationFormProps {
  formProps: FormProps;
  /** Valores transformados (IDs planos) para inicializar el formulario */
  initialValuesModified?: any;
  edit?: boolean;
}

/** Largo mínimo del CEP brasileño para disparar la búsqueda automática */
const BRASIL_CEP_LENGTH = 8;
/** Extrae solo dígitos de un valor de código postal */
const toPostalCode = (value?: unknown) => {
  if (value === null || value === undefined) return '';

  return String(value as string).replace(/\D/g, '');
};

/** Estado inicial del lookup de CEP: sin carga, sin error, sin datos */
const initialCepState = {
  loading: false,
  location: undefined as any,
  error: false,
  canEditLocationManually: false,
  canEditAddressManually: false,
};

/**
 * Hook que centraliza la lógica de ubicación (país→provincia→localidad) y lookup de CEP para el formulario de Shipper.
 * Inspirado en useLogisticAddressForm pero sin lógica de ShippingLabels/direcciones guardadas.
 */
export const useShipperLocationForm = ({
  formProps,
  initialValuesModified,
}: UseShipperLocationFormProps) => {
  const { packageRepository } = useRepositoriesContext();
  const { isBrasilSelected } = useCountryContext();
  const { form } = formProps;
  // Inicialización de valores del formulario via useFormInitialSet.
  // En creación setea el país default; en edición setea todos los campos planos.
  const { initialValuesSetted } = useFormInitialSet(form, initialValuesModified, []);
  // Observar campos del formulario para reaccionar a cambios y manejar dependencias
  const pais = Form.useWatch('pais', form);
  const provincia = Form.useWatch('provincia', form);
  const localidad = Form.useWatch('localidad', form);
  const postalCode = Form.useWatch('postal_code', form);
  // Debounce del código postal para evitar llamadas excesivas al API de CEP
  const debouncedPostalCode = useDebouncedValue(postalCode, 500);
  const normalizedPostalCode = useMemo(
    () => toPostalCode(debouncedPostalCode),
    [debouncedPostalCode],
  );
  // El lookup de CEP se habilita solo si el contexto es Brasil y el código tiene 8+ dígitos
  const isCepLookupEnabled = isBrasilSelected && normalizedPostalCode.length >= BRASIL_CEP_LENGTH;
  const [cepLookupState, setCepLookupState] = useState(initialCepState);
  // Metadata: evita reseteos dependientes cuando se setean valores programáticamente (CEP lookup).
  const skipNextLocationResetRef = useRef(false);
  const previousLocationRef = useRef({
    pais: undefined as any,
    provincia: undefined as any,
    localidad: undefined as any,
    postalCode: undefined as any,
  });
  // Selects encadenados: país → provincia → localidad.
  // Cada select filtra por el valor del nivel superior.
  const { selectProps: paisesSelectProps } = useSelectScrollSearch(
    {
      resource: 'paises',
      optionLabel: (item: any) => `${item.nombre}`,
    },
    { searchField: 'nombre' },
  );
  const { selectProps: provinciasSelectProps } = useSelectScrollSearch(
    {
      resource: 'provincias',
      optionLabel: (item: any) => `${item.nombre}`,
      filters: [
        {
          value: pais,
          operator: 'eq' as const,
          field: 'pais',
        },
      ],
      queryOptions: {
        enabled: !!pais && !paisesSelectProps.loading,
      },
    },
    { searchField: 'nombre' },
  );
  const { selectProps: localidadesSelectProps } = useSelectScrollSearch(
    {
      resource: 'localidades',
      optionLabel: (item: any) => `${item.nombre}`,
      filters: [
        {
          value: provincia,
          operator: 'eq' as const,
          field: 'provincia',
        },
      ],
      queryOptions: {
        enabled: !provinciasSelectProps.loading && !!pais && !!provincia,
      },
    },
    {
      saveItem: true,
      searchField: 'nombre',
    },
  );
  // Carga opciones iniciales (por ID) para que los selects muestren el label correcto en edición
  const paisesSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: paisesSelectProps,
    resource: 'paises',
    initialValue: initialValuesModified?.pais,
    labelKey: 'nombre',
    valueKey: 'id',
  });
  const provinciasSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: provinciasSelectProps,
    resource: 'provincias',
    initialValue: initialValuesModified?.provincia,
    labelKey: 'nombre',
    valueKey: 'id',
  });
  const localidadesSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: localidadesSelectProps,
    resource: 'localidades',
    initialValue: initialValuesModified?.localidad,
    labelKey: 'nombre',
    valueKey: 'id',
    getOption: (data: any) => ({
      label: data.nombre,
      value: data.id,
    }),
  });
  // Helpers para setear campos sin disparar reseteos dependientes
  const setProgrammaticFields = useCallback(
    (values: Record<string, unknown>) => {
      if (!form) return;
      // Marca que el próximo cambio detectado es programático, no del usuario
      skipNextLocationResetRef.current = true;
      form.setFieldsValue(values);
    },
    [form],
  );
  const resetCepState = useCallback(() => {
    setCepLookupState(initialCepState);
  }, []);
  const clearCepResolvedFields = useCallback(() => {
    setProgrammaticFields({
      provincia: undefined,
      localidad: undefined,
      address: undefined,
    });
  }, [setProgrammaticFields]);
  // Opciones de provincia enriquecidas: agrega la provincia del CEP si no está en las opciones cargadas
  const provinciasOptions = useMemo(() => {
    const options = provinciasSelectPropsWithInitials.options || [];
    const cepProvincia = cepLookupState.location?.provincia;

    if (cepProvincia && !options.some((option: any) => option.value === cepProvincia.id)) {
      return [{ value: cepProvincia.id, label: cepProvincia.nombre }, ...options];
    }

    return options;
  }, [cepLookupState.location?.provincia, provinciasSelectPropsWithInitials.options]);
  // Opciones de localidad enriquecidas: agrega la ciudad del CEP si no está en las opciones cargadas
  const localidadesOptions = useMemo(() => {
    const options = localidadesSelectPropsWithInitials.options || [];
    const cepCiudad = cepLookupState.location?.ciudad;

    if (cepCiudad && !options.some((option: any) => option.value === cepCiudad.id)) {
      return [
        { value: cepCiudad.id, label: cepCiudad.nombre, postal_code: postalCode },
        ...options,
      ];
    }

    return options;
  }, [cepLookupState.location?.ciudad, localidadesSelectPropsWithInitials.options, postalCode]);

  // Inicializar ref de ubicación previa cuando se setean valores iniciales
  useEffect(() => {
    if (initialValuesSetted && form) {
      previousLocationRef.current = {
        pais,
        provincia: form.getFieldValue('provincia'),
        localidad: form.getFieldValue('localidad'),
        postalCode: form.getFieldValue('postal_code'),
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValuesSetted]);

  // Reseteo dependiente: cambio de pais/provincia/localidad/postalCode
  useEffect(() => {
    if (!form || !initialValuesSetted) return;

    if (skipNextLocationResetRef.current) {
      previousLocationRef.current = { pais, provincia, localidad, postalCode };
      skipNextLocationResetRef.current = false;

      return;
    }

    const previousLocation = previousLocationRef.current;

    // Brasil: si cambia el CEP, resetear provincia/localidad/address
    if (isBrasilSelected && postalCode !== previousLocation.postalCode) {
      form.setFieldsValue({
        provincia: undefined,
        localidad: undefined,
        address: undefined,
      });
      resetCepState();
      previousLocationRef.current = {
        pais,
        provincia: undefined,
        localidad: undefined,
        postalCode,
      };

      return;
    }

    // Si cambia el país, provincia/localidad/código postal dejan de ser confiables
    if (pais !== previousLocation.pais) {
      form.setFieldsValue({
        provincia: undefined,
        localidad: undefined,
        postal_code: undefined,
      });
      previousLocationRef.current = {
        pais,
        provincia: undefined,
        localidad: undefined,
        postalCode: undefined,
      };

      return;
    }

    // Si cambia la provincia, localidad/código postal deben elegirse nuevamente
    if (provincia !== previousLocation.provincia) {
      form.setFieldsValue({
        localidad: undefined,
        ...(!isBrasilSelected ? { postal_code: undefined } : {}),
      });
      previousLocationRef.current = {
        pais,
        provincia,
        localidad: undefined,
        postalCode: isBrasilSelected ? postalCode : undefined,
      };

      return;
    }

    if (!isBrasilSelected && localidad !== previousLocation.localidad) {
      if (!localidad) {
        form.setFieldsValue({ postal_code: undefined });
      }

      previousLocationRef.current = { pais, provincia, localidad, postalCode };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais, provincia, localidad, postalCode, initialValuesSetted, isBrasilSelected]);

  // Consulta al API de CEP (solo se ejecuta cuando isCepLookupEnabled es true)
  const {
    data: cepData,
    error: cepError,
    isLoading: cepLoading,
  } = useQuery({
    queryKey: ['locationByCep', 'shipper', normalizedPostalCode],
    queryFn: () => packageRepository.getLocationFromCEP(normalizedPostalCode),
    enabled: isCepLookupEnabled,
    retry: false,
    staleTime: 0,
  });

  // Efecto que procesa la respuesta del CEP y autocompleta provincia/localidad/dirección
  useEffect(() => {
    if (!isBrasilSelected) {
      resetCepState();

      return;
    }

    if (!isCepLookupEnabled) {
      resetCepState();
      clearCepResolvedFields();

      return;
    }

    if (cepLoading) {
      setCepLookupState({ ...initialCepState, loading: true });

      return;
    }

    if (cepError || !cepData) {
      setCepLookupState({ ...initialCepState, error: !!cepError });
      clearCepResolvedFields();

      return;
    }

    setCepLookupState({
      ...initialCepState,
      location: cepData,
      canEditAddressManually: !cepData?.direccion,
    });
    setProgrammaticFields({
      provincia: cepData?.provincia?.id,
      localidad: cepData?.ciudad?.id,
      address: cepData?.direccion,
    });
  }, [
    isBrasilSelected,
    isCepLookupEnabled,
    cepData,
    cepError,
    cepLoading,
    clearCepResolvedFields,
    resetCepState,
    setProgrammaticFields,
  ]);

  // Al seleccionar localidad en modo no-Brasil, autocompleta el código postal si está disponible
  const handleLocalidadChange = useCallback(
    (value: SelectProps['value'], option: any) => {
      localidadesSelectPropsWithInitials.onChange?.(value, option);

      if (form && initialValuesSetted && option) {
        form.setFieldsValue({ postal_code: option.item?.postal_code });
      }
    },
    [form, initialValuesSetted, localidadesSelectPropsWithInitials],
  );
  // Permite al usuario editar manualmente UF/Municipio cuando el CEP no retornó resultados
  const enableBrazilManualLocation = useCallback(() => {
    setCepLookupState(current => ({ ...current, canEditLocationManually: true }));
  }, []);

  return {
    pais,
    provincia,
    isBrasilSelected,
    initialValuesSetted,
    paisesSelectProps: paisesSelectPropsWithInitials,
    provinciasSelectProps: { ...provinciasSelectPropsWithInitials, options: provinciasOptions },
    localidadesSelectProps: localidadesSelectPropsWithInitials,
    localidadesOptions,
    cepLookupState,
    handleLocalidadChange,
    enableBrazilManualLocation,
  };
};
